import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGamePlayers } from '../../../hooks/useGamePlayers';
import { useShieldStatus } from '../../../hooks/useShieldStatus';
import type { UtPlayerInfo } from '../../../hooks/useUtPlayerIndex';
import { formatXp } from '../format';
import { Panel, PlayerRow, SectionTitle, StatusPill, UtButton, UtLoading } from '../primitives';
import { UtCard } from '../UtCard';
import { shortDate, shortDateTime } from './dates';
import { UtDropoutModal } from './UtDropoutModal';

interface Props {
  gameId: string;
  matchNumber?: number | string;
  date?: string;
  playerId: string | null;
  currentStreak: number;
  index: Map<string, UtPlayerInfo>;
  onChanged: () => Promise<void>;
}

/**
 * Players announced, teams not yet: who's in, reserves in offer order, who
 * dropped out, and the viewer's own status with a drop-out action. Not in the
 * design; built from the same UT pieces. Data: useGamePlayers.
 */
export const PlayersAnnounced: React.FC<Props> = ({ gameId, matchNumber, date, playerId, currentStreak, index, onChanged }) => {
  const { selectedPlayers, reservePlayers, droppedOutPlayers, absentPlayers, isLoading, refreshPlayers } = useGamePlayers(gameId);
  const { shieldStatus } = useShieldStatus(playerId ?? undefined);
  const [dropout, setDropout] = useState(false);

  if (isLoading) return <UtLoading label="Loading players" />;

  const me = playerId ? [...selectedPlayers, ...reservePlayers].find((p) => p.id === playerId) : undefined;
  const card = (p: { id: string; friendly_name: string; xp?: number; whatsapp_group_member?: string | null }) => {
    const info = index.get(p.id);
    return {
      id: p.id,
      name: p.friendly_name,
      xp: info?.xp ?? p.xp ?? 0,
      rarity: info?.rarity,
      position: info?.position,
      whatsappMember: p.whatsapp_group_member === 'Yes' || p.whatsapp_group_member === 'Proxy',
      photo: info?.photo,
    };
  };

  return (
    <div className="flex flex-col gap-[18px]">
      {me && (
        <Panel tone="accent" className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="font-ut-display text-[22px] font-extrabold text-ut-acc">
              {me.status === 'selected' ? "YOU'RE IN" : "YOU'RE A RESERVE"}
            </span>
            <span className="text-sm">
              {me.status === 'selected'
                ? `Selected ${me.selection_method === 'random' ? 'in the random draw' : 'on XP'}${me.using_token ? ' with a priority token' : ''}.`
                : 'You get offered a place if someone drops out.'}
            </span>
            {me.hasSlotOffer && me.slotOfferStatus === 'pending' && (
              <Link to="/notifications" className="mt-1 text-sm font-semibold text-ut-acc">
                You&apos;ve been offered a slot. Respond in your inbox →
              </Link>
            )}
          </div>
          <UtButton variant="secondary" size="sm" onClick={() => setDropout(true)}>
            Drop out
          </UtButton>
        </Panel>
      )}

      <div className="flex flex-col gap-3">
        <SectionTitle note={`${selectedPlayers.length} players · sorted by XP`}>Selected</SectionTitle>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(84px,1fr))] gap-2">
          {selectedPlayers.map((p) => (
            <UtCard key={p.id} player={card(p)} size="mini" highlight={p.id === playerId} />
          ))}
        </div>
      </div>

      {reservePlayers.length > 0 && (
        <div className="flex flex-col gap-2">
          <SectionTitle note="In order of slot offers">Reserves</SectionTitle>
          {reservePlayers.map((p, i) => {
            const c = card(p);
            const offer = p.hasSlotOffer ? p.slotOfferStatus : null;
            return (
              <Panel key={p.id} pad="none" className="overflow-hidden border-0">
                <PlayerRow
                  to={`/players/${p.id}`}
                  playerId={p.id}
                  name={p.friendly_name}
                  rarity={c.rarity}
                  xp={c.xp}
                  lead={<span className="w-5 font-ut-display text-base font-bold text-ut-mut">{i + 1}</span>}
                  sub={`${c.position ?? '—'} · ${formatXp(c.xp)} XP${p.potentialOfferTimes?.available_at ? ` · offer from ${shortDateTime(p.potentialOfferTimes.available_at)}` : ''}`}
                  right={offer ? <StatusPill tone={offer === 'pending' ? 'gold' : 'mut'}>{offer === 'pending' ? 'Offered' : 'Declined'}</StatusPill> : undefined}
                />
              </Panel>
            );
          })}
        </div>
      )}

      {(droppedOutPlayers.length > 0 || absentPlayers.length > 0) && (
        <div className="flex flex-col gap-2">
          <SectionTitle>Not playing</SectionTitle>
          <div className="flex flex-wrap gap-1.5">
            {droppedOutPlayers.map((p) => (
              <StatusPill key={p.id} tone="bad">
                {p.friendly_name} · dropped out{p.using_shield ? ' · shield' : ''}
              </StatusPill>
            ))}
            {absentPlayers.map((p) => (
              <StatusPill key={p.id} tone="sky">
                {p.friendly_name} · {p.using_injury ? 'injured' : 'shield'}
              </StatusPill>
            ))}
          </div>
        </div>
      )}

      {me && playerId && (
        <UtDropoutModal
          open={dropout}
          onClose={() => setDropout(false)}
          playerId={playerId}
          gameId={gameId}
          matchNumber={matchNumber}
          dateLabel={date ? shortDate(date) : undefined}
          isSelected={me.status === 'selected'}
          currentStreak={currentStreak}
          shieldTokensAvailable={shieldStatus?.tokensAvailable ?? 0}
          onDone={async () => {
            await refreshPlayers();
            await onChanged();
          }}
        />
      )}
    </div>
  );
};
